import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Quote, UserPlus } from "lucide-react";
import { SmartImage } from "@/components/SmartImage";

// Personal "letter" from the distributor, displayed beside the Join My Team form.
// Content is fully editable in Admin -> Nilalaman -> "Kwento ni Sam (About Me)".
export const AboutMe = ({ aboutMe = {}, config = {}, onCta }) => {
  const a = aboutMe || {};
  const photo = a.photo || config.distributorPhoto;
  const name = a.signatureName || config.distributorName || "";
  const paragraphs = (a.paragraphs || []).filter((p) => p && p.trim());
  const roles = (a.roles || []).filter((r) => r && r.trim());

  return (
    <Card
      className="relative h-full overflow-hidden rounded-2xl border-border bg-card p-6 text-card-foreground shadow-xl sm:p-8"
      data-testid="about-me-card"
    >
      <Quote className="pointer-events-none absolute -right-4 -top-4 h-32 w-32 text-accent/10" aria-hidden="true" />

      {a.eyebrow ? (
        <p className="text-sm font-semibold uppercase tracking-wider text-accent" data-testid="about-me-eyebrow">{a.eyebrow}</p>
      ) : null}

      <div className="mt-4 flex items-center gap-4 sm:gap-5">
        <div
          className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-muted shadow-[0_10px_30px_-12px_rgba(15,23,42,0.45)] ring-2 ring-accent/40 sm:h-28 sm:w-28"
          data-testid="about-me-photo"
        >
          <SmartImage src={photo} alt={name} fit="cover" fallbackLabel={name ? name.split(" ")[0] : "Sam"} />
        </div>
        <div className="min-w-0">
          <h3 className="font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl" data-testid="about-me-greeting">{a.greeting}</h3>
          {a.tagline ? <p className="mt-1 text-sm italic text-muted-foreground" data-testid="about-me-tagline">{a.tagline}</p> : null}
        </div>
      </div>

      <div className="mt-6 space-y-3 text-[15px] leading-relaxed text-foreground/85" data-testid="about-me-body">
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      {roles.length ? (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="about-me-roles">
          {roles.map((r, i) => (
            <Badge key={i} variant="secondary" className="rounded-md px-2.5 py-1 text-xs font-medium">{r}</Badge>
          ))}
        </div>
      ) : null}

      <div className="mt-6 flex flex-col gap-4 border-t border-border pt-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-display text-2xl italic text-primary" data-testid="about-me-signature">{name}</p>
          {a.signatureRole ? <p className="mt-0.5 text-xs text-muted-foreground">{a.signatureRole}</p> : null}
        </div>
        {a.ctaLabel ? (
          <Button
            type="button"
            className="h-11 bg-accent text-accent-foreground hover:bg-accent/90 focus-visible:ring-accent"
            onClick={onCta}
            data-testid="about-me-cta-button"
          >
            <UserPlus className="mr-2 h-4 w-4" /> {a.ctaLabel}
          </Button>
        ) : null}
      </div>
    </Card>
  );
};
